import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Plus, Users, ChevronRight } from 'lucide-react'
import AppShell from '../components/AppShell.jsx'
import Card from '../components/ui/Card.jsx'
import Button from '../components/ui/Button.jsx'
import Modal from '../components/ui/Modal.jsx'
import Input, { Field } from '../components/ui/Input.jsx'
import { SkeletonCard } from '../components/ui/Skeleton.jsx'
import { useToast } from '../components/ui/Toast.jsx'
import { listClients, createClient_ } from '../lib/api.js'

export default function Clients() {
  const [clients, setClients] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [form, setForm] = useState({ name: '', contact_name: '', contact_email: '' })
  const [busy, setBusy] = useState(false)
  const toast = useToast()

  async function refresh() {
    setClients(await listClients())
  }
  useEffect(() => { refresh() }, [])

  async function handleCreate(e) {
    e.preventDefault()
    if (!form.name.trim()) return
    setBusy(true)
    try {
      await createClient_({
        name: form.name.trim(),
        contact_name: form.contact_name.trim() || null,
        contact_email: form.contact_email.trim() || null
      })
      setModalOpen(false)
      setForm({ name: '', contact_name: '', contact_email: '' })
      toast('Client created', 'success')
      refresh()
    } catch (err) {
      toast(err.message, 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <AppShell>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display font-bold text-[24px]">Clients</h1>
          <p className="text-muted text-[14px] mt-1">Your own end-customers — group their flipbooks here.</p>
        </div>
        <Button icon={Plus} onClick={() => setModalOpen(true)}>New client</Button>
      </div>

      {clients === null ? (
        <div className="grid grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => <SkeletonCard key={i} />)}
        </div>
      ) : clients.length === 0 ? (
        <Card className="p-10 text-center">
          <Users size={32} className="mx-auto mb-3 text-muted-2" />
          <p className="text-muted text-[14px] mb-4">No clients yet. Add the first one to start building flipbooks.</p>
          <Button icon={Plus} className="mx-auto" onClick={() => setModalOpen(true)}>New client</Button>
        </Card>
      ) : (
        <div className="grid grid-cols-3 gap-4">
          {clients.map((c) => (
            <Link key={c.id} to={`/clients/${c.id}`}>
              <Card hover className="p-5 cursor-pointer h-full flex flex-col justify-between">
                <div>
                  <div className="font-semibold text-[15px] mb-1">{c.name}</div>
                  {c.contact_name && <div className="text-[12.5px] text-muted">{c.contact_name}</div>}
                  {c.contact_email && <div className="text-[12.5px] text-muted-2">{c.contact_email}</div>}
                </div>
                <div className="flex items-center justify-end mt-4 text-sky text-[13px] font-medium">
                  Open <ChevronRight size={15} />
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="New client">
        <form onSubmit={handleCreate}>
          <Field label="Client name">
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Company XYZ" required autoFocus />
          </Field>
          <Field label="Contact name (optional)">
            <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
          </Field>
          <Field label="Contact email (optional)">
            <Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
          </Field>
          <Button type="submit" className="w-full mt-2" loading={busy}>Create client</Button>
        </form>
      </Modal>
    </AppShell>
  )
}
